import {
  createContext, Dispatch, PropsWithChildren, SetStateAction, useContext, useMemo, useState,
} from 'react';
import { useCasesContext } from '../cases';
import { useAssigneesContext } from '../assignees';

type CasesUIContextType = {
  refreshData: () => Promise<void>;
  key: number;
  loading: boolean;
  onlyMyApplications: boolean;
  lastRetrieved: Date;
  filterStatus: string[],
  setLastRetrieved: Dispatch<SetStateAction<Date>>;
  setOnlyMyApplications: Dispatch<SetStateAction<boolean>>;
  setFilterStatus: Dispatch<SetStateAction<string[]>>;
}

const defaultCasesContext: CasesUIContextType = {
  refreshData: async () => { },
  key: 0,
  loading: false,
  onlyMyApplications: false,
  lastRetrieved: new Date(),
  filterStatus: [],
  setLastRetrieved: () => { },
  setOnlyMyApplications: () => { },
  setFilterStatus: () => { },
};

const CasesUIContext = createContext<CasesUIContextType>(defaultCasesContext);

export function useCasesUIContext() {
  return useContext(CasesUIContext);
}

export function CasesUIProvider({ children }: PropsWithChildren) {
  const { fetchCases } = useCasesContext();
  const { fetchAssignees } = useAssigneesContext();
  const [lastRetrieved, setLastRetrieved] = useState(new Date());
  const [key, setKey] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [onlyMyApplications, setOnlyMyApplications] = useState<boolean>(false);
  const [filterStatus, setFilterStatus] = useState<string[]>([]);

  const refreshData = async () => {
    setLastRetrieved(new Date());
    setLoading(true);
    setKey((prev) => prev + 1);
    setOnlyMyApplications(false);
    try {
      await Promise.all([fetchCases(), fetchAssignees()]);
    } finally {
      setLoading(false);
    }
  };

  const contextValue = useMemo(() => ({
    refreshData,
    key,
    loading,
    onlyMyApplications,
    filterStatus,
    lastRetrieved,
    setLastRetrieved,
    setOnlyMyApplications,
    setFilterStatus,
  }), [key, loading, onlyMyApplications, lastRetrieved, filterStatus]);

  return (
    <CasesUIContext.Provider value={contextValue}>
      {children}
    </CasesUIContext.Provider>
  );
}
